"use client"

import type React from "react"
import { observer } from "mobx-react-lite"
import useTitle from "../../shared/hooks/useTitle"
import { useEffect, useState } from "react"
import useBackButton from "../../shared/hooks/useBack"
import { useAppContext } from "../../shared/functions/Context"
import { LoadingEllipsis } from "../../shared/components/loading/Loading"
import ErrorBoundary from "../../shared/components/error-boundary/ErrorBoundary"
import DisputeRequests from "./DisputeRequests"
import DisputeDashboard from "./DisputeDashboard"
import DisputeLog from "./dispute-log/DisputeLog"
import { FileText, CheckCircle, Clock, TrendingUp, MessageSquare, AlertCircle } from "lucide-react"

interface ITabsProps {
  tab: "Dispute Dashboard" | "Dispute Requests" | "Dispute Log" | "Reports"
  setTab: React.Dispatch<React.SetStateAction<"Dispute Dashboard" | "Dispute Requests" | "Dispute Log" | "Reports">>
}

const Tabs = observer((props: ITabsProps) => {
  const { store } = useAppContext()
  const role = store.auth.role

  const activeClass = (tab: "Dispute Dashboard" | "Dispute Requests" | "Dispute Log" | "Reports") => {
    return props.tab === tab
      ? "border-b-2 border-[#84cbe9] text-[#3f6a8e] font-semibold"
      : "text-gray-600 hover:text-[#3f6a8e] hover:border-b-2 hover:border-gray-300"
  }

  return (
    <div className="border-b border-gray-200 mb-8">
      <nav className="flex gap-8">
        <button
          className={`pb-4 px-2 transition-all ${activeClass("Dispute Dashboard")}`}
          onClick={() => props.setTab("Dispute Dashboard")}
        >
          Dispute Dashboard
        </button>
        <button
          className={`pb-4 px-2 transition-all ${activeClass("Dispute Requests")}`}
          onClick={() => props.setTab("Dispute Requests")}
        >
          Dispute Requests
        </button>
        <button
          className={`pb-4 px-2 transition-all ${activeClass("Dispute Log")}`}
          onClick={() => props.setTab("Dispute Log")}
        >
          Dispute Log
        </button>
      </nav>
    </div>
  )
})

const DisputeHome = observer(() => {
  const { api, store, ui } = useAppContext()
  const scorecard = store.scorecard.activeId

  useTitle("Dispute Management")
  useBackButton()

  const [tab, setTab] = useState<"Dispute Dashboard" | "Dispute Requests" | "Dispute Log" | "Reports">(
    "Dispute Dashboard",
  )
  const [loading, setLoading] = useState(false)

  const allScorecards = store.individualScorecard.all
  const measures = store.measure.all

  const disputedUids = allScorecards
    .filter((scorecard) => scorecard.asJson.isDispute)
    .map((disputedScorecard) => disputedScorecard.asJson.uid)

  // Count midterm (Q2) disputes - based on disputeReason or isDispute flag on measures
  const midtermDisputesCount = measures.filter(
    (measure) => measure.asJson.disputeReason || measure.asJson.isDispute,
  ).length

  // Count final assessment (Q4) disputes - based on finalAssessmentDisputeReason or finalAssessmentIsDispute flag
  const finalAssessmentDisputesCount = measures.filter(
    (measure) => measure.asJson.finalAssessmentDisputeReason || measure.asJson.finalAssessmentIsDispute,
  ).length

  const totalDisputesLogged = midtermDisputesCount + finalAssessmentDisputesCount
  
  const resolvedDisputesCount = measures.filter(
    (measure) => 
      (measure.asJson.isResolved && (measure.asJson.disputeReason || measure.asJson.isDispute)) ||
      (measure.asJson.finalAssessmentIsResolved && (measure.asJson.finalAssessmentDisputeReason || measure.asJson.finalAssessmentIsDispute))
  ).length
  
  const pendingDisputes = totalDisputesLogged - resolvedDisputesCount
  const totalScorecards = allScorecards.length
  const disputePercentage = totalScorecards ? ((disputedUids.length / totalScorecards) * 100).toFixed(1) : "0"

  useEffect(() => {
    const loadAll = async () => {
      if (!scorecard) return

      setLoading(true)
      try {
        await api.user.getAll()
        await api.department.getAll()
        await api.companyMeasure.getAll(scorecard)
        await api.companyObjective.getAll(scorecard)
        await api.individualScorecard.getAll()
        await api.measure.getAll()
      } catch (error) {
        ui.snackbar.load({
          id: Date.now(),
          type: "danger",
          message: "Failed to load.",
          timeoutInMs: 10000,
        })
      }
      setLoading(false)
    }

    loadAll()
  }, [
    api.companyMeasure,
    api.companyObjective,
    api.department,
    api.user,
    api.individualScorecard,
    api.measure,
    scorecard,
    ui.snackbar,
  ])

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <div className="bg-gradient-to-r from-[#3f6a8e] to-[#84cbe9] rounded-xl shadow-lg p-8 mb-8">
              <h1 className="text-4xl font-bold text-white mb-2">Dispute Management</h1>
              <p className="text-blue-100 text-lg">
                Monitor, review, and resolve scorecard disputes across your organization
              </p>
            </div>

            {/* Stats Dashboard */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
              {/* Total Disputes */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="w-5 h-5 text-[#3f6a8e]" />
                  </div>
                  <h3 className="text-sm font-medium text-gray-600">Total Disputes</h3>
                </div>
                <p className="text-3xl font-bold text-[#3f6a8e]">{totalDisputesLogged}</p>
              </div>

              {/* Q2 (Midterm) Disputes */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <MessageSquare className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="text-sm font-medium text-gray-600">Q2 (Midterm)</h3>
                </div>
                <p className="text-3xl font-bold text-purple-600">{midtermDisputesCount}</p>
              </div>

              {/* Q4 (Final Assessment) Disputes */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-[#faa05a]" />
                  </div>
                  <h3 className="text-sm font-medium text-gray-600">Q4 (Final Assessment)</h3>
                </div>
                <p className="text-3xl font-bold text-[#faa05a]">{finalAssessmentDisputesCount}</p>
              </div>

              {/* Resolved Disputes */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-[#4bb543]" />
                  </div>
                  <h3 className="text-sm font-medium text-gray-600">Resolved</h3>
                </div>
                <p className="text-3xl font-bold text-[#4bb543]">{resolvedDisputesCount}</p>
              </div>

              {/* Pending Disputes */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Clock className="w-5 h-5 text-[#e7a637]" />
                  </div>
                  <h3 className="text-sm font-medium text-gray-600">Pending</h3>
                </div>
                <p className="text-3xl font-bold text-[#e7a637]">{pendingDisputes}</p>
              </div>
            </div>
          </div>

          <ErrorBoundary>
            <Tabs tab={tab} setTab={setTab} />
          </ErrorBoundary>

          <ErrorBoundary>{loading && <LoadingEllipsis />}</ErrorBoundary>

          <ErrorBoundary>
            {tab === "Dispute Dashboard" && !loading && <DisputeDashboard />}
            {tab === "Dispute Requests" && !loading && <DisputeRequests />}
            {tab === "Dispute Log" && !loading && <DisputeLog />}
          </ErrorBoundary>
        </div>
      </div>
    </ErrorBoundary>
  )
})

export default DisputeHome
